import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { queryKeys, type QueryKeyPart } from "@nasam/kernel";
import type {
  ArchivedAssetTypesListQuery,
  AssetTypeCreateInput,
  AssetTypesListQuery,
  AssetTypesRepository,
  AssetTypeUpdateInput,
} from "../domain";

function params(
  query: AssetTypesListQuery | ArchivedAssetTypesListQuery | undefined,
): Readonly<Record<string, QueryKeyPart>> | undefined {
  return query === undefined ? undefined : { ...query };
}

export function useAssetTypesQuery(
  repository: AssetTypesRepository,
  query?: AssetTypesListQuery,
) {
  return useQuery({
    queryKey: queryKeys.assetTypes.list(params(query)),
    queryFn: () => repository.list(query),
  });
}

export function useAssetTypeSummaryQuery(repository: AssetTypesRepository) {
  return useQuery({
    queryKey: queryKeys.assetTypes.summary(),
    queryFn: () => repository.getSummary(),
  });
}

export function useAssetTypeQuery(
  repository: AssetTypesRepository,
  id: string,
) {
  return useQuery({
    queryKey: queryKeys.assetTypes.detail(id),
    queryFn: () => repository.getById(id),
    enabled: id.length > 0,
  });
}

export function useCreateAssetTypeMutation(repository: AssetTypesRepository) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: AssetTypeCreateInput) => repository.create(input),
    // An archived match created nothing, so only a real creation touches the list.
    onSuccess: async (result) => {
      if (result.status === "created") {
        await client.invalidateQueries({ queryKey: queryKeys.assetTypes.all });
      }
    },
  });
}

export function useRestoreAssetTypeMutation(repository: AssetTypesRepository) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => repository.restore(id),
    onSuccess: async () =>
      client.invalidateQueries({ queryKey: queryKeys.assetTypes.all }),
  });
}

export function useArchivedAssetTypesQuery(
  repository: AssetTypesRepository,
  query?: ArchivedAssetTypesListQuery,
) {
  return useQuery({
    // Nested under assetTypes.lists() so a restore's `assetTypes.all`
    // invalidation also refetches this list (the restored row drops out).
    queryKey: [...queryKeys.assetTypes.lists(), "archived", params(query) ?? {}],
    queryFn: () => repository.listArchived(query),
  });
}

export function useUpdateAssetTypeMutation(repository: AssetTypesRepository) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: AssetTypeUpdateInput }) =>
      repository.update(id, input),
    onSuccess: async () =>
      client.invalidateQueries({ queryKey: queryKeys.assetTypes.all }),
  });
}

export function useDeleteAssetTypeMutation(repository: AssetTypesRepository) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => repository.delete(id),
    onSuccess: async () =>
      client.invalidateQueries({ queryKey: queryKeys.assetTypes.all }),
  });
}
